/**
 * OnboardingContext - State Machine & Persistence
 *
 * Manages the guided tour lifecycle:
 * - State machine: idle → active → navigating → targeting → showing → complete
 * - Persistence: localStorage (primary) + Supabase preferences (backup)
 * - Navigation: cross-page routing with element wait polling
 * - Auto-start: triggers for new users without saved state
 *
 * The OnboardingProvider must be placed INSIDE BrowserRouter, AuthProvider,
 * and LanguageProvider to access routing, auth, and i18n.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useUserStatus } from '@/contexts/UserStatusContext';
import {
  OnboardingState,
  OnboardingStep,
  ONBOARDING_TOUR_VERSION,
  ONBOARDING_STORAGE_KEY,
  SlideType,
  CarouselStyle,
  Profile,
} from '@/types';
import {
  ONBOARDING_CHAPTERS,
  findChapter,
  getChapterIndex,
  PHASE1_CHAPTER_IDS,
  PHASE2_CHAPTER_IDS,
  PHASE1_LAST_CHAPTER_ID,
  PHASE2_FIRST_CHAPTER_ID,
} from '@/lib/onboardingTour';
import {
  resolveRoute,
  routeMatches,
  isMobileViewport,
  waitForElement,
} from '@/lib/onboardingUtils';
import { getProfile, saveProfile, ProfilePreferences } from '@/services/profileService';
import { listProjects, saveProject } from '@/services/projectService';
import { generateId } from '@/lib/utils';

// ============================================================================
// TYPES
// ============================================================================

interface OnboardingContextType {
  /** Current onboarding state */
  state: OnboardingState;
  /** Whether the tour overlay should be visible */
  isOverlayVisible: boolean;
  /** Current chapter object */
  currentChapter: ReturnType<typeof findChapter>;
  /** Current step object */
  currentStep: OnboardingStep | null;
  /** Start the tour from the beginning */
  startTour: () => void;
  /** Move to the next step */
  nextStep: () => void;
  /** Move to the previous step */
  prevStep: () => void;
  /** Skip/dismiss the tour */
  dismissTour: () => void;
  /** Restart the tour from scratch */
  restartTour: () => void;
  /** Whether the tour has been completed or dismissed */
  isCompleted: boolean;
  /** Notify that the dashboard is ready (all setup modals completed) */
  notifyDashboardReady: () => void;
  /** Notify that the chat page is ready (all setup modals completed). Alias of notifyDashboardReady for the new chat-first entry point. */
  notifyChatReady: () => void;
}

const defaultState: OnboardingState = {
  isActive: false,
  currentChapterId: ONBOARDING_CHAPTERS[0]?.id || '',
  currentStepIndex: 0,
  completedChapterIds: [],
  dismissed: false,
  completedAt: null,
  version: ONBOARDING_TOUR_VERSION,
  phase: 'phase1',
};

const OnboardingContext = createContext<OnboardingContextType>({
  state: defaultState,
  isOverlayVisible: false,
  currentChapter: undefined,
  currentStep: null,
  startTour: () => {},
  nextStep: () => {},
  prevStep: () => {},
  dismissTour: () => {},
  restartTour: () => {},
  isCompleted: false,
  notifyDashboardReady: () => {},
  notifyChatReady: () => {},
});

export const useOnboarding = () => useContext(OnboardingContext);

// ============================================================================
// PERSISTENCE HELPERS (user-scoped via _userId field)
// ============================================================================

interface StoredOnboardingState extends OnboardingState {
  _userId?: string;
}

function loadFromLocalStorage(userId?: string): OnboardingState | null {
  try {
    const raw = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredOnboardingState;
    if (parsed.version !== ONBOARDING_TOUR_VERSION) return null;
    // If a userId is provided, validate ownership
    if (userId && parsed._userId && parsed._userId !== userId) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveToLocalStorage(state: OnboardingState, userId?: string): void {
  try {
    const data: StoredOnboardingState = { ...state, _userId: userId };
    localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // localStorage might be full or unavailable
  }
}

function clearLocalStorage(): void {
  try {
    localStorage.removeItem(ONBOARDING_STORAGE_KEY);
  } catch {
    // Ignore
  }
}

// ============================================================================
// DEMO PROJECT
// ============================================================================

const DEMO_SLIDES = [
  {
    id: generateId(),
    type: SlideType.COVER,
    content: '# Welcome to Core Carousel\n\nThis is a demo carousel to explore the editor.',
    showImage: false,
  },
  {
    id: generateId(),
    type: SlideType.CONTENT,
    content: '## Slide 2\n\nYou can edit text, add images, and customize the style of each slide.',
    showImage: false,
  },
  {
    id: generateId(),
    type: SlideType.CONTENT,
    content: '## Slide 3\n\nUse AI to generate images or upload your own photos.',
    showImage: false,
  },
  {
    id: generateId(),
    type: SlideType.CONTENT,
    content: '## Slide 4\n\nCustomize fonts, colors, and layout to match your brand.',
    showImage: false,
  },
  {
    id: generateId(),
    type: SlideType.CTA,
    content: '## Follow for more!\n\nLike, save, and share this carousel.',
    showImage: false,
  },
];

// ============================================================================
// PROVIDER
// ============================================================================

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  // Troca obrigatória de senha pendente: o tour não pode "roubar" a navegação
  // nem abrir o overlay por cima de /change-password.
  const { passwordChangePending } = useUserStatus();

  const [state, setState] = useState<OnboardingState>(() => {
    return loadFromLocalStorage() || { ...defaultState };
  });
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);

  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initializedForUserRef = useRef<string | null>(null);
  const profileCheckedForUserRef = useRef<string | null>(null);
  const isNavigatingRef = useRef(false);
  // Refs for dashboard-ready signaling (persist across StrictMode re-runs)
  const dashboardReadyRef = useRef(false);
  const pendingNewUserRef = useRef(false);

  // Current chapter & step derived from state
  const currentChapter = findChapter(state.currentChapterId);
  const currentStep = currentChapter?.steps[state.currentStepIndex] ?? null;
  const isCompleted = state.dismissed || state.completedAt !== null;

  // ============================================================================
  // PERSISTENCE
  // ============================================================================

  const persistState = useCallback((newState: OnboardingState) => {
    saveToLocalStorage(newState, user?.id);
    setState(newState);
  }, [user?.id]);

  // Debounced Supabase sync
  const syncToSupabase = useCallback((newState: OnboardingState) => {
    if (!user?.id) return;
    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);

    syncTimeoutRef.current = setTimeout(async () => {
      try {
        const profile = await getProfile(user.id);
        if (profile) {
          const prefs: ProfilePreferences = {
            ...(profile.preferences || {}),
            onboardingState: newState,
          };
          await saveProfile(user.id, profile, prefs);
        }
      } catch (err) {
        // Onboarding sync to Supabase failed - non-blocking
      }
    }, 3000);
  }, [user?.id]);

  // ============================================================================
  // NAVIGATION
  // ============================================================================

  const navigateToStep = useCallback(async (
    chapter: NonNullable<ReturnType<typeof findChapter>>,
    step: OnboardingStep,
    newState: OnboardingState,
  ) => {
    const stepRoute = step.route || chapter.route;
    const resolvedRoute = resolveRoute(stepRoute, newState.projectId);
    const currentPath = location.pathname + location.search;

    if (!routeMatches(stepRoute, currentPath, newState.projectId)) {
      isNavigatingRef.current = true;
      setIsOverlayVisible(false);
      navigate(resolvedRoute);

      // Wait for target element to appear instead of fixed delay
      const selector = step.targetSelector;
      if (selector && step.spotlight) {
        await waitForElement(selector, 3000);
      } else {
        // For centered/modal steps, just wait for route transition
        await new Promise((resolve) => setTimeout(resolve, 400));
      }
      isNavigatingRef.current = false;
    }

    // Dispatch beforeShowAction if step requires it (e.g. open a flyout menu)
    if (step.beforeShowAction) {
      window.dispatchEvent(new CustomEvent('cai:tour-action', {
        detail: { action: step.beforeShowAction },
      }));
      await new Promise((resolve) => setTimeout(resolve, 300));
    }

    setIsOverlayVisible(true);
  }, [location.pathname, location.search, navigate]);

  // ============================================================================
  // ENSURE PROJECT FOR EDITOR CHAPTERS
  // ============================================================================

  const ensureEditorProject = useCallback(async (newState: OnboardingState): Promise<OnboardingState> => {
    if (newState.demoProjectId) return newState;

    try {
      // Check if user has existing projects
      const projects = await listProjects();
      if (projects && projects.length > 0) {
        // Use the most recent project
        const latest = projects[0];
        return { ...newState, demoProjectId: latest.id };
      }

      // No projects - create a demo
      if (!user?.id) return newState;

      const profileData = await getProfile(user.id);
      const profile: Profile = profileData
        ? { name: profileData.name, handle: profileData.handle, avatarUrl: profileData.avatarUrl }
        : { name: 'User', handle: 'user', avatarUrl: '' };

      const demoProjectId = generateId();
      const now = new Date().toISOString();

      await saveProject({
        id: demoProjectId,
        name: 'Tour Demo',
        style: CarouselStyle.TWITTER,
        aspectRatio: '4/5',
        profile,
        slides: DEMO_SLIDES,
        theme: 'DARK',
        accentColor: '#8b5cf6',
        showAccent: true,
        showSlideNumbers: true,
        showVerifiedBadge: false,
        headerScale: 1,
        fontStyle: 'MODERN',
        fontScale: 1,
        globalImageStyle: '',
        isDemo: true,
        createdAt: now,
        updatedAt: now,
      }, user.id);

      return { ...newState, demoProjectId };
    } catch (err) {
      // Failed to ensure editor project - non-blocking
      return newState;
    }
  }, [user?.id]);

  // ============================================================================
  // STATE MACHINE ACTIONS
  // ============================================================================

  const startTour = useCallback(() => {
    const newState: OnboardingState = {
      ...defaultState,
      isActive: true,
      currentRoute: '/',
      phase: 'phase1',
    };
    persistState(newState);
    setIsOverlayVisible(true);

    // Navigate to dashboard if not there
    if (location.pathname !== '/') {
      navigate('/');
    }
  }, [persistState, location.pathname, navigate]);

  const nextStep = useCallback(async () => {
    if (!currentChapter || !currentStep) return;

    const isMobile = isMobileViewport();
    let nextChapterId = state.currentChapterId;
    let nextStepIndex = state.currentStepIndex + 1;
    let completedChapterIds = [...state.completedChapterIds];

    // Determine which chapters are in scope for the current phase
    const phaseChapterIds = state.phase === 'phase2' ? PHASE2_CHAPTER_IDS : PHASE1_CHAPTER_IDS;

    // Find next visible step (skip desktopOnly on mobile, mobileOnly on desktop)
    // Scoped to current phase only
    const findNextVisibleStep = (chapterIdx: number, stepIdx: number): { chapterIdx: number; stepIdx: number } | null => {
      for (let ci = chapterIdx; ci < ONBOARDING_CHAPTERS.length; ci++) {
        const ch = ONBOARDING_CHAPTERS[ci];
        if (!phaseChapterIds.includes(ch.id)) continue; // Skip chapters outside current phase
        const startStep = ci === chapterIdx ? stepIdx : 0;
        for (let si = startStep; si < ch.steps.length; si++) {
          const step = ch.steps[si];
          if (isMobile && step.desktopOnly) continue;
          if (!isMobile && step.mobileOnly) continue;
          return { chapterIdx: ci, stepIdx: si };
        }
      }
      return null;
    };

    // Check if we've finished the current chapter
    if (nextStepIndex >= currentChapter.steps.length) {
      if (!completedChapterIds.includes(currentChapter.id)) {
        completedChapterIds.push(currentChapter.id);
      }

      // Phase 1 complete — pause tour and wait for user to create a real project
      if (currentChapter.id === PHASE1_LAST_CHAPTER_ID && state.phase === 'phase1') {
        const waitingState: OnboardingState = {
          ...state,
          isActive: false,
          completedChapterIds,
          phase: 'waiting',
          currentChapterId: PHASE2_FIRST_CHAPTER_ID,
          currentStepIndex: 0,
        };
        persistState(waitingState);
        syncToSupabase(waitingState);
        setIsOverlayVisible(false);
        window.dispatchEvent(new CustomEvent('cai:tour-phase1-complete'));
        navigate('/');
        return;
      }

      const currentChapterIndex = getChapterIndex(currentChapter.id);
      const nextVisibleStep = findNextVisibleStep(currentChapterIndex + 1, 0);

      if (!nextVisibleStep) {
        // Phase complete
        const finalState: OnboardingState = {
          ...state,
          isActive: false,
          completedChapterIds,
          completedAt: new Date().toISOString(),
        };
        persistState(finalState);
        syncToSupabase(finalState);
        setIsOverlayVisible(false);
        if (state.phase !== 'phase2') {
          navigate('/');
        }
        return;
      }

      const nextChapter = ONBOARDING_CHAPTERS[nextVisibleStep.chapterIdx];
      nextChapterId = nextChapter.id;
      nextStepIndex = nextVisibleStep.stepIdx;
    } else {
      // Check if next step in current chapter is visible
      const currentChapterIndex = getChapterIndex(currentChapter.id);
      const nextVisibleInChapter = findNextVisibleStep(currentChapterIndex, nextStepIndex);

      if (nextVisibleInChapter && ONBOARDING_CHAPTERS[nextVisibleInChapter.chapterIdx].id === currentChapter.id) {
        nextStepIndex = nextVisibleInChapter.stepIdx;
      } else {
        // No more visible steps in this chapter, move to next
        if (!completedChapterIds.includes(currentChapter.id)) {
          completedChapterIds.push(currentChapter.id);
        }

        // Phase 1 complete — pause tour
        if (currentChapter.id === PHASE1_LAST_CHAPTER_ID && state.phase === 'phase1') {
          const waitingState: OnboardingState = {
            ...state,
            isActive: false,
            completedChapterIds,
            phase: 'waiting',
            currentChapterId: PHASE2_FIRST_CHAPTER_ID,
            currentStepIndex: 0,
          };
          persistState(waitingState);
          syncToSupabase(waitingState);
          setIsOverlayVisible(false);
          window.dispatchEvent(new CustomEvent('cai:tour-phase1-complete'));
          navigate('/');
          return;
        }

        if (!nextVisibleInChapter) {
          // Phase complete
          const finalState: OnboardingState = {
            ...state,
            isActive: false,
            completedChapterIds,
            completedAt: new Date().toISOString(),
          };
          persistState(finalState);
          syncToSupabase(finalState);
          setIsOverlayVisible(false);
          if (state.phase !== 'phase2') {
            navigate('/');
          }
          return;
        }

        nextChapterId = ONBOARDING_CHAPTERS[nextVisibleInChapter.chapterIdx].id;
        nextStepIndex = nextVisibleInChapter.stepIdx;
      }
    }

    const newState: OnboardingState = {
      ...state,
      currentChapterId: nextChapterId,
      currentStepIndex: nextStepIndex,
      completedChapterIds,
    };

    const nextChapter = findChapter(nextChapterId);
    const nextStepObj = nextChapter?.steps[nextStepIndex];

    if (nextChapter && nextStepObj) {
      const route = nextStepObj.route || nextChapter.route;
      newState.currentRoute = resolveRoute(route, newState.projectId);
    }

    persistState(newState);

    // Navigate if needed
    if (nextChapter && nextStepObj) {
      await navigateToStep(nextChapter, nextStepObj, newState);
    }
  }, [state, currentChapter, currentStep, persistState, syncToSupabase, navigateToStep, navigate]);

  const prevStep = useCallback(async () => {
    if (!currentChapter) return;

    // Block backward navigation across phase boundary
    if (state.phase === 'phase2' && currentChapter.id === PHASE2_FIRST_CHAPTER_ID && state.currentStepIndex === 0) {
      return; // Already at Phase 2 start, can't go back to Phase 1
    }

    const isMobile = isMobileViewport();
    let prevChapterId = state.currentChapterId;
    let prevStepIndex = state.currentStepIndex - 1;

    // Scope to current phase
    const phaseChapterIds = state.phase === 'phase2' ? PHASE2_CHAPTER_IDS : PHASE1_CHAPTER_IDS;

    const findPrevVisibleStep = (chapterIdx: number, stepIdx: number): { chapterIdx: number; stepIdx: number } | null => {
      for (let ci = chapterIdx; ci >= 0; ci--) {
        const ch = ONBOARDING_CHAPTERS[ci];
        if (!phaseChapterIds.includes(ch.id)) continue; // Skip chapters outside current phase
        const startStep = ci === chapterIdx ? stepIdx : ch.steps.length - 1;
        for (let si = startStep; si >= 0; si--) {
          const step = ch.steps[si];
          if (isMobile && step.desktopOnly) continue;
          if (!isMobile && step.mobileOnly) continue;
          return { chapterIdx: ci, stepIdx: si };
        }
      }
      return null;
    };

    if (prevStepIndex < 0) {
      // Go back to previous chapter
      const currentChapterIndex = getChapterIndex(currentChapter.id);
      const prevChapterSteps = ONBOARDING_CHAPTERS[currentChapterIndex - 1]?.steps;
      const prevVisible = findPrevVisibleStep(currentChapterIndex - 1, prevChapterSteps ? prevChapterSteps.length - 1 : 0);
      if (!prevVisible) return; // Already at the very first step of this phase

      const prevChapter = ONBOARDING_CHAPTERS[prevVisible.chapterIdx];
      prevChapterId = prevChapter.id;
      prevStepIndex = prevVisible.stepIdx;
    } else {
      const currentChapterIndex = getChapterIndex(currentChapter.id);
      const prevVisible = findPrevVisibleStep(currentChapterIndex, prevStepIndex);
      if (!prevVisible) return;

      if (ONBOARDING_CHAPTERS[prevVisible.chapterIdx].id !== currentChapter.id) {
        prevChapterId = ONBOARDING_CHAPTERS[prevVisible.chapterIdx].id;
      }
      prevStepIndex = prevVisible.stepIdx;
    }

    const prevChapter = findChapter(prevChapterId);
    const prevStepObj = prevChapter?.steps[prevStepIndex];

    const newState: OnboardingState = {
      ...state,
      currentChapterId: prevChapterId,
      currentStepIndex: prevStepIndex,
      currentRoute: prevChapter && prevStepObj
        ? resolveRoute(prevStepObj.route || prevChapter.route, state.projectId)
        : state.currentRoute,
    };

    persistState(newState);

    if (prevChapter && prevStepObj) {
      await navigateToStep(prevChapter, prevStepObj, newState);
    }
  }, [state, currentChapter, persistState, navigateToStep]);

  const dismissTour = useCallback(() => {
    const newState: OnboardingState = {
      ...state,
      isActive: false,
      dismissed: true,
    };
    persistState(newState);
    syncToSupabase(newState);
    setIsOverlayVisible(false);
  }, [state, persistState, syncToSupabase]);

  const restartTour = useCallback(() => {
    clearLocalStorage();
    startTour();
  }, [startTour]);

  // ============================================================================
  // AUTO-START FOR NEW USERS
  // ============================================================================

  // Activate tour for a new user — called from notifyDashboardReady or from
  // the init effect when dashboard-ready was already signaled.
  const activateNewUserTour = useCallback(() => {
    const existing = initializedForUserRef.current
      ? loadFromLocalStorage(initializedForUserRef.current)
      : null;
    // Don't activate if already active, previously dismissed, already completed,
    // in the waiting phase (between phase1 and phase2), or has any completed chapters.
    if (existing && (existing.isActive || existing.dismissed || existing.completedAt || existing.phase === 'waiting' || (existing.completedChapterIds && existing.completedChapterIds.length > 0))) {
      return;
    }
    const freshState: OnboardingState = {
      ...defaultState,
      isActive: true,
      currentRoute: '/',
      phase: 'phase1',
    };
    persistState(freshState);
    setIsOverlayVisible(true);
  }, [persistState]);

  // Called by DashboardPage when all setup modals (profile + avatar) are done.
  // Uses refs instead of closure variables so it works regardless of effect
  // lifecycle (StrictMode double-mount, re-renders, etc.).
  const notifyDashboardReady = useCallback(() => {
    dashboardReadyRef.current = true;
    if (pendingNewUserRef.current) {
      setTimeout(activateNewUserTour, 500);
    }
  }, [activateNewUserTour]);

  useEffect(() => {
    if (authLoading) return;
    if (passwordChangePending) return;
    const userId = user?.id;
    if (!userId) return;
    if (initializedForUserRef.current === userId) return;

    // Only proceed on protected routes (not /login, /reset, etc.)
    const isProtectedRoute =
      location.pathname === '/' ||
      location.pathname === '/create' ||
      location.pathname.startsWith('/editor/');
    if (!isProtectedRoute) return;

    // Reset refs for this initialization cycle
    dashboardReadyRef.current = false;
    pendingNewUserRef.current = false;

    // Also listen for the legacy event (backward compatibility / non-context callers)
    const handleDashboardReadyEvent = () => notifyDashboardReady();
    window.addEventListener('cai:dashboard-ready', handleDashboardReadyEvent);

    // --- ASYNC: check localStorage and Supabase ---
    const initOnboarding = async () => {
      // 1. Check local state for THIS user
      const localState = loadFromLocalStorage(userId);
      if (localState) {
        initializedForUserRef.current = userId;
        setState(localState);
        if (localState.isActive && !localState.dismissed && !localState.completedAt) {
          setTimeout(() => setIsOverlayVisible(true), 1000);
        }
        return;
      }

      // Clear any stale state from a different user
      clearLocalStorage();

      // 2. Check Supabase for existing state (only once per user)
      if (profileCheckedForUserRef.current !== userId) {
        profileCheckedForUserRef.current = userId;
        try {
          const profile = await getProfile(userId);
          const supabaseState = profile?.preferences?.onboardingState;

          if (supabaseState && supabaseState.version === ONBOARDING_TOUR_VERSION) {
            initializedForUserRef.current = userId;
            persistState(supabaseState);
            if (supabaseState.isActive && !supabaseState.dismissed && !supabaseState.completedAt) {
              setTimeout(() => setIsOverlayVisible(true), 1000);
            }
            return;
          }
        } catch {
          // Ignore Supabase errors - proceed as new user
        }
      }

      // 3. New user (no state anywhere) → wait for dashboard to be ready
      if (location.pathname === '/') {
        initializedForUserRef.current = userId;
        pendingNewUserRef.current = true;

        // If dashboard-ready was already signaled during our async checks, activate now
        if (dashboardReadyRef.current) {
          setTimeout(activateNewUserTour, 500);
        }
      }
    };

    initOnboarding();

    return () => {
      window.removeEventListener('cai:dashboard-ready', handleDashboardReadyEvent);
      // If tour was never activated (e.g. user redirected during init),
      // allow re-initialization on next visit — but only if the tour
      // was never dismissed or completed (prevents re-activation loop).
      const savedState = loadFromLocalStorage(userId);
      const neverActivated = !savedState?.isActive && !savedState?.dismissed && !savedState?.completedAt && savedState?.phase !== 'waiting' && !(savedState?.completedChapterIds && savedState.completedChapterIds.length > 0);
      if (pendingNewUserRef.current && neverActivated) {
        initializedForUserRef.current = null;
      }
    };
  }, [user?.id, authLoading, passwordChangePending, persistState, location.pathname, notifyDashboardReady, activateNewUserTour]);

  // ============================================================================
  // PHASE 2 AUTO-RESUME: When user enters editor with own project
  // ============================================================================
  //
  // Split into two effects so the state transition (waiting → phase2) does not
  // retrigger the same effect's cleanup and kill its own setTimeout — that race
  // left the tour "active but invisible" and the overlay never appeared.

  // Effect 1: transition waiting → phase2 the first time the user lands in
  // the editor. This runs exactly once per waiting-state entry.
  useEffect(() => {
    if (!user?.id) return;
    if (state.phase !== 'waiting') return;
    if (state.dismissed) return;

    const editorMatch = location.pathname.match(/^\/editor\/(.+)$/);
    if (!editorMatch) return;

    const projectId = editorMatch[1];
    persistState({
      ...state,
      isActive: true,
      phase: 'phase2',
      projectId,
      currentChapterId: PHASE2_FIRST_CHAPTER_ID,
      currentStepIndex: 0,
      currentRoute: `/editor/${projectId}`,
    });
  }, [location.pathname, state.phase, state.dismissed, user?.id, persistState]);

  // Effect 2: after entering phase2 on an editor route, show the overlay
  // once. Depends only on the conditions that should gate visibility — not
  // on state.phase's transitional lifecycle — so the scheduled timeout is
  // not nuked by the prior effect's cleanup.
  useEffect(() => {
    if (state.phase !== 'phase2') return;
    if (!state.isActive) return;
    if (state.dismissed) return;
    if (isOverlayVisible) return;
    if (!location.pathname.startsWith('/editor/')) return;

    // Small delay so the editor renders before overlay appears
    const timeoutId = setTimeout(() => setIsOverlayVisible(true), 1500);
    return () => clearTimeout(timeoutId);
  }, [state.phase, state.isActive, state.dismissed, isOverlayVisible, location.pathname]);

  // ============================================================================
  // KEYBOARD NAVIGATION
  // ============================================================================

  useEffect(() => {
    if (!isOverlayVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        e.preventDefault();
        dismissTour();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.stopPropagation();
        e.preventDefault();
        nextStep();
      } else if (e.key === 'ArrowLeft') {
        e.stopPropagation();
        e.preventDefault();
        prevStep();
      }
    };

    // Capture phase to intercept before other handlers
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOverlayVisible, dismissTour, nextStep, prevStep]);

  // ============================================================================
  // CLEANUP
  // ============================================================================

  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    };
  }, []);

  // ============================================================================
  // CONTEXT VALUE
  // ============================================================================

  const value: OnboardingContextType = {
    state,
    isOverlayVisible: isOverlayVisible && !passwordChangePending,
    currentChapter,
    currentStep,
    startTour,
    nextStep,
    prevStep,
    dismissTour,
    restartTour,
    isCompleted,
    notifyDashboardReady,
    notifyChatReady: notifyDashboardReady,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export default OnboardingContext;
